import type { SlugUrlConfig, ParamPassThrough } from '@playcastdotio/Messaging';
import type { CapturedDynamicParams } from './utmCapture';

/**
 * Target outbound URL type for pass-through matrix lookup
 */
export type OutboundTarget = 'iosCta' | 'iosTracker' | 'androidCta' | 'androidTracker';

/**
 * Build an outbound URL by applying the pass-through matrix.
 * Appends captured dynamic params and custom UTMs to the base URL
 * based on the slug's pass-through configuration.
 */
export function buildOutboundUrl(
  baseUrl: string,
  capturedParams: CapturedDynamicParams,
  slugConfig: SlugUrlConfig,
  target: OutboundTarget,
): string {
  if (!baseUrl) return baseUrl;

  let normalizedUrl = baseUrl;
  if (!/^https?:\/\//i.test(normalizedUrl)) {
    normalizedUrl = 'https://' + normalizedUrl;
  }

  try {
    const url = new URL(normalizedUrl);
    const { params: paramRules, customUtms } = slugConfig.passThrough;

    // Apply dynamic inbound param pass-through rules
    for (const rule of paramRules) {
      if (rule[target] && capturedParams[rule.paramKey]) {
        url.searchParams.set(rule.paramKey, capturedParams[rule.paramKey]);
      }
    }

    // Apply custom UTM pass-through (all-or-nothing for the group)
    if (customUtms[target]) {
      // Custom UTMs are the standard utm_* params captured from the URL
      // that are NOT in the dynamic inbound params list
      const dynamicKeys = new Set(slugConfig.dynamicInboundParams.map(p => p.key));
      const utmKeys = Object.keys(capturedParams).filter(
        k => k.startsWith('utm_') && !dynamicKeys.has(k)
      );
      for (const key of utmKeys) {
        if (capturedParams[key]) {
          url.searchParams.set(key, capturedParams[key]);
        }
      }
    }

    return url.toString();
  } catch {
    return baseUrl;
  }
}

/**
 * Parse custom UTM parameters from URL path
 * 
 * Format: /q/slug~pn~{partner}~cn~{campaign}~cid~{campaignId}~asid~{adsetId}~crid~{creativeId}
 * Maps to: pn→source, cn→campaign, cid→id, asid→term, crid→content
 * 
 * @returns Object with custom UTM values (only if they exist in path)
 */
const parseCustomPathParams = (): Partial<Record<'utm_source' | 'utm_campaign' | 'utm_id' | 'utm_term' | 'utm_content', string>> => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    // Extract slug portion from path (e.g., /q/shades~pn~google~cn~winter)
    const pathMatch = window.location.pathname.match(/^\/q\/([^/?]+)/);
    if (!pathMatch) return {};

    const fullSlug = pathMatch[1];
    const parts = fullSlug.split('~');

    // Parse tilde-separated key-value pairs
    const customParams: Record<string, string> = {};
    for (let i = 1; i < parts.length; i += 2) {
      const key = parts[i];
      const value = parts[i + 1];
      if (key && value) {
        customParams[key] = value;
      }
    }

    // Map custom params to UTM params
    return {
      utm_source: customParams['pn'] || undefined,     // Partner name → source
      utm_campaign: customParams['cn'] || undefined,   // Campaign name → campaign
      utm_id: customParams['cid'] || undefined,        // Campaign ID → id
      utm_term: customParams['asid'] || undefined,     // Ad set ID → term
      utm_content: customParams['crid'] || undefined,  // Creative ID → content
    };
  } catch {
    return {};
  }
};

/**
 * @deprecated Legacy Steam-specific UTM enrichment. Only applies to steampowered.com URLs.
 * For new campaigns using slug URL configs, use getOutboundCtaUrl() instead which
 * delegates to the pass-through matrix system (buildOutboundUrl at theme-load time).
 * This function is preserved only for backward compatibility with campaigns that
 * do not yet have a slug config.
 */
export const buildQuickstartSteamUrl = (steamUrl: string): string => {
  if (typeof window === 'undefined') {
    return steamUrl;
  }

  // Ensure the URL has a protocol so it's treated as absolute, not relative
  let normalizedUrl = steamUrl;
  if (!/^https?:\/\//i.test(normalizedUrl)) {
    normalizedUrl = 'https://' + normalizedUrl;
  }

  try {
    const url = new URL(normalizedUrl);

    // Only apply UTM enrichment to Steam store URLs.
    // Non-Steam URLs (App Store, Singular, etc.) have their own tracking
    // parameters that should not be mutated.
    if (!url.hostname.includes('steampowered.com')) {
      return steamUrl;
    }

    const steamParams = url.searchParams;
    const pageParams = new URLSearchParams(window.location.search);

    // Parse custom path parameters as fallbacks
    const customParams = parseCustomPathParams();

    // Collect all sources that need to go into utm_medium
    const mediumParts: string[] = [];

    // 1. Get existing utm_source from Steam URL (if any)
    const steamUtmSource = steamParams.get('utm_source');
    if (steamUtmSource) {
      mediumParts.push(steamUtmSource);
    }

    // 2. Get utm_medium from page URL (if any)
    const pageUtmMedium = pageParams.get('utm_medium');
    if (pageUtmMedium) {
      mediumParts.push(pageUtmMedium);
    }

    // 3. Get utm_source from page URL query params OR custom path params (query takes priority)
    const pageUtmSource = pageParams.get('utm_source') || customParams.utm_source;
    if (pageUtmSource) {
      mediumParts.push(pageUtmSource);
    }

    // 4. Set utm_source to always be playcast_dropin
    steamParams.set('utm_source', 'playcast_dropin');

    // 5. Combine all sources into utm_medium
    if (mediumParts.length > 0) {
      steamParams.set('utm_medium', mediumParts.join('_'));
    }

    // 6. Set other UTM parameters (use query params first, fall back to custom path params)
    const utmParamsToSet = {
      utm_campaign: pageParams.get('utm_campaign') || customParams.utm_campaign,
      utm_id: pageParams.get('utm_id') || customParams.utm_id,
      utm_term: pageParams.get('utm_term') || customParams.utm_term,
      utm_content: pageParams.get('utm_content') || customParams.utm_content,
    };

    Object.entries(utmParamsToSet).forEach(([key, value]) => {
      if (value) {
        steamParams.set(key, value);
      }
    });

    // 7. Copy any remaining UTM parameters from page URL query (in case there are others)
    pageParams.forEach((value, key) => {
      if (!key.startsWith('utm_')) {
        return;
      }
      // Skip parameters we've already handled
      if (['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content'].includes(key)) {
        return;
      }
      if (value) {
        steamParams.set(key, value);
      }
    });

    url.search = steamParams.toString();
    return url.toString();
  } catch {
    return normalizedUrl;
  }
};

/**
 * Resolve the final outbound CTA URL.
 * When slug config is active, the URL is already fully enriched via buildOutboundUrl
 * at theme-load time — use it directly. Otherwise, fall back to the legacy
 * Steam-specific UTM enrichment for un-migrated campaigns.
 */
export function getOutboundCtaUrl(ctaUrl: string, hasSlugConfig: boolean): string {
  if (hasSlugConfig) {
    return ctaUrl;
  }
  return buildQuickstartSteamUrl(ctaUrl);
}

/**
 * Theme URL fields relevant to slug config enrichment.
 * Subset of QuickstartTheme to keep this utility decoupled from the full theme type.
 */
export interface ThemeUrlFields {
  steamUrl?: string;
  steamUrlIos?: string;
  usePerDeviceUrls?: boolean;
  onLoadPixelUrl?: string;
  onLoadPixelUrlIos?: string;
}

/**
 * Enrich theme URL fields with slug-config URLs + pass-through params.
 *
 * Rules:
 * - CTA: Android CTA is required (enforced by admin). If iOS CTA exists, each
 *   platform gets its own enriched URL. If only Android CTA exists, iOS also
 *   uses the Android enriched URL (prevents stale theme values from leaking).
 * - Trackers: Independent per platform. No cross-platform sharing.
 * - Pass-through target always matches the URL's configured platform.
 */
export function enrichThemeUrls(
  theme: ThemeUrlFields,
  slugConfig: SlugUrlConfig,
  capturedParams: CapturedDynamicParams,
  isIos: boolean,
): ThemeUrlFields {
  const enriched = { ...theme };

  if (slugConfig.android.ctaUrl) {
    const enrichedAndroidCta = buildOutboundUrl(slugConfig.android.ctaUrl, capturedParams, slugConfig, 'androidCta');
    enriched.steamUrl = enrichedAndroidCta;

    if (isIos && slugConfig.ios.ctaUrl) {
      enriched.steamUrlIos = buildOutboundUrl(slugConfig.ios.ctaUrl, capturedParams, slugConfig, 'iosCta');
    } else if (isIos) {
      enriched.steamUrlIos = enrichedAndroidCta;
    }
    enriched.usePerDeviceUrls = true;
  }

  if (slugConfig.android.trackerUrl) {
    enriched.onLoadPixelUrl = buildOutboundUrl(slugConfig.android.trackerUrl, capturedParams, slugConfig, 'androidTracker');
  }
  if (isIos && slugConfig.ios.trackerUrl) {
    enriched.onLoadPixelUrlIos = buildOutboundUrl(slugConfig.ios.trackerUrl, capturedParams, slugConfig, 'iosTracker');
    enriched.usePerDeviceUrls = true;
  }

  return enriched;
}

